import { api } from '@/hooks/api'
import { useCallback, useEffect, useRef, useState } from 'react'

/** Quantas notificações o painel do sino mostra — o resto só aparece no modal. */
export const PREVIEW_SIZE = 10

const PER_PAGE = 10

interface NotificationsMeta {
  total: number
  last_page: number
  current_page: number
}

export interface NotificationsFeed {
  items: Notification[]
  /** Primeira carga: o painel ainda não tem o que mostrar. */
  loading: boolean
  /** Páginas seguintes, pedidas pelo botão do modal. */
  loadingMore: boolean
  total: number
  hasMore: boolean
  /** Não lidas entre as do painel — é o número que o sino exibe. */
  unreadCount: number
  isRead: (notification: Notification) => boolean
  loadMore: () => void
  markAsRead: (id: number) => void
}

/**
 * Carrega o feed de notificações do usuário logado, uma página por vez.
 *
 * A lista é única: o painel do sino mostra as `PREVIEW_SIZE` primeiras e o modal mostra todas as
 * que já foram carregadas, então abrir o modal não refaz a busca que o painel já tinha feito.
 */
export function useNotifications(): NotificationsFeed {
  const [items, setItems] = useState<Notification[]>([])
  const [meta, setMeta] = useState<NotificationsMeta>({
    total: 0,
    last_page: 1,
    current_page: 1
  })
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [readIds, setReadIds] = useState<number[]>([])

  // Evita que dois cliques seguidos no "Carregar mais" peçam a mesma página.
  const loadingPage = useRef(0)

  const load = useCallback(async (page: number) => {
    if (loadingPage.current === page) {
      return
    }

    loadingPage.current = page

    if (page === 1) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const { data } = await api.get('/notifications', {
        params: { page, per_page: PER_PAGE }
      })

      setMeta(data.meta)
      setItems(current => {
        const merged = page === 1 ? data.data : [...current, ...data.data]

        /* A paginação é por deslocamento: se uma notificação nova entrar entre dois cliques de
           "Carregar mais", a página seguinte repete um registro que já está na lista. */
        return merged.filter(
          (item: Notification, index: number) =>
            merged.findIndex((other: Notification) => other.id === item.id) ===
            index
        )
      })
    } catch (err) {
      //
    }

    loadingPage.current = 0
    setLoading(false)
    setLoadingMore(false)
  }, [])

  useEffect(() => {
    load(1)
  }, [load])

  const loadMore = useCallback(() => {
    if (meta.current_page >= meta.last_page) {
      return
    }

    load(meta.current_page + 1)
  }, [load, meta.current_page, meta.last_page])

  /* `readed` nasce nulo no banco (a coluna não tem default), então só `true` conta como lida. */
  const isRead = useCallback(
    (notification: Notification) =>
      Boolean(notification.readed) || readIds.includes(notification.id),
    [readIds]
  )

  const markAsRead = useCallback(
    async (id: number) => {
      if (readIds.includes(id)) {
        return
      }

      setReadIds(current => [...current, id])

      try {
        await api.put(`/notifications/${id}`)
      } catch (err) {
        setReadIds(current => current.filter(readId => readId !== id))
      }
    },
    [readIds]
  )

  return {
    items,
    loading,
    loadingMore,
    total: meta.total,
    hasMore: meta.current_page < meta.last_page,
    unreadCount: items
      .slice(0, PREVIEW_SIZE)
      .filter(notification => !isRead(notification)).length,
    isRead,
    loadMore,
    markAsRead
  }
}
